import { useEffect, useState } from 'react'
import axios from 'axios'
import { useGetToken } from './useGetToken'

// Shape returned by GET /v1/me (authorizer-resolved access; see backend
// services/users/getMyAccess.ts). Lets the frontend learn what the caller may do
// without probing a data endpoint.
export type MyAccess = {
  email: string
  role: string
  authProvider: string
  permissions: string[]
  allowedCountries: string[]
  allowedVendorIds: string[] | null
}

// Cached for the session so navigating between routes does not re-hit /v1/me.
let cachedAccess: MyAccess | null = null
let inflight: Promise<MyAccess | null> | null = null

export const clearMyAccessCache = (): void => {
  cachedAccess = null
  inflight = null
}

/**
 * A caller is treated as an admin (and lands on /admin) when they hold the
 * admin user-read permission (or the super-admin wildcard).
 */
export const isAdminAccess = (access: MyAccess | null): boolean =>
  !!access &&
  (access.permissions.includes('*') || access.permissions.includes('admin:user:read'))

/**
 * Fetch (and cache) the caller's access from GET /v1/me. Returns loading/error
 * so callers (e.g. role-aware landing) can render a spinner and fall back
 * gracefully when access cannot be resolved.
 */
export const useMyAccess = (): {
  access: MyAccess | null
  loading: boolean
  error: boolean
} => {
  const [getToken] = useGetToken()
  const [access, setAccess] = useState<MyAccess | null>(cachedAccess)
  const [loading, setLoading] = useState<boolean>(!cachedAccess)
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    let active = true

    const run = async () => {
      if (cachedAccess) {
        setAccess(cachedAccess)
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        if (!inflight) {
          inflight = (async () => {
            const token = await getToken()
            if (!token) {
              // No token yet: reset the shared promise so a later mount can
              // retry once a token becomes available (otherwise every mount
              // reuses this resolved Promise<null> and never refetches).
              inflight = null
              return null
            }
            const res = await axios.get(
              `${process.env.REACT_APP_API_ENDPOINT}/v1/me`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                },
              },
            )
            const data = res.data || {}
            const value: MyAccess = {
              email: data.email || '',
              role: data.role || '',
              authProvider: data.authProvider || '',
              permissions: data.permissions || [],
              allowedCountries: data.allowedCountries || [],
              allowedVendorIds: data.allowedVendorIds ?? null,
            }
            cachedAccess = value
            return value
          })()
        }
        const value = await inflight
        if (!active) return
        if (value) {
          setAccess(value)
        } else {
          setError(true)
        }
      } catch (err) {
        // Reset the shared promise so a later mount can retry.
        inflight = null
        console.warn('useMyAccess: failed to resolve /v1/me', err)
        if (active) setError(true)
      } finally {
        if (active) setLoading(false)
      }
    }

    run()
    return () => {
      active = false
    }
  }, [getToken])

  return { access, loading, error }
}
